import axios from './BaseService';
import { getBaseUrl } from './ServiceUtils';
import appRequest from './appRequest';

const STRATEGIES_URL = getBaseUrl('/strategies/');

export async function getStrategies(symbol = '', page = 1, size, environment, market, onlyActive) {
    const params = new URLSearchParams({ page, symbol });
    if (size) params.set('size', size);
    if (environment) params.set('environment', environment);
    if (market) params.set('market', market);
    if (onlyActive) params.set('onlyActive', 'true');
    return await appRequest.doGetRequest(`${STRATEGIES_URL}?${params}`);
}

export async function getStrategy(id) {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}${id}`);
}

// Detector de ociosidade: "por que o bot não está operando?" (convergência de regime — design vs bug).
export async function getIdleAnalysis(environment) {
    const params = environment ? `?env=${environment}` : '';
    return await appRequest.doGetRequest(`${STRATEGIES_URL}idle-analysis${params}`);
}

export async function getLaunchRadar() {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}launch-radar`);
}

export async function prepareLaunch(payload) {
    // payload: { symbol } ou { symbols: [] } + presets opcionais (usdtAmount/interval/stopLossPct/...)
    return await appRequest.doPostRequest(`${STRATEGIES_URL}prepare-launch`, payload);
}

export async function setLaunchAlerts(enabled) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}launch-alerts`, { enabled });
}

export async function setLaunchEnabled(enabled) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}launch-enabled`, { enabled });
}

export async function addSupportingAutomation(strategyId, automationId) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${strategyId}/supporting/${automationId}`, {});
}

export async function removeSupportingAutomation(strategyId, automationId) {
    return await appRequest.doDeleteRequest(`${STRATEGIES_URL}${strategyId}/supporting/${automationId}`);
}

export async function getAllStrategies(symbol, onlyActives) {
    const params = {};
    if (symbol) params.symbol = symbol;
    if (onlyActives) params.onlyActives = onlyActives;
    const requestParams = new URLSearchParams(params).toString();

    return await appRequest.doGetRequest(`${STRATEGIES_URL}all/?${requestParams}`);
}

export async function getSharedStrategies(symbol = '', includePublic = true, page = 1) {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}shared?page=${page}&symbol=${symbol}&public=${includePublic}`);
}

export async function saveStrategy(id, newStrategy) {
    if (id)
        return await appRequest.doPatchRequest(`${STRATEGIES_URL}${id}`, newStrategy);
    else
        return await appRequest.doPostRequest(STRATEGIES_URL, newStrategy);
}

export async function deleteStrategy(id) {
    return await appRequest.doDeleteRequest(`${STRATEGIES_URL}${id}`);
}

export async function startStrategy(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/start`, {});
}

export async function stopStrategy(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/stop`, {});
}

export async function copyStrategy(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/copy`, {});
}

export async function simulateStrategy(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/simulate`, {});
}

// Orquestrador por regime: rotula a estratégia pelo teste sintético (deriva good/bad do sweep).
export async function labelStrategyRegime(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/regime-label`, {});
}

// Sweep sintético read-only (aba "Sintético" da modal "Testar estratégia") — não persiste rótulo.
export async function getRegimeSweep(id, symbol) {
    const params = symbol ? `?symbol=${symbol}` : '';
    return await appRequest.doGetRequest(`${STRATEGIES_URL}${id}/regime-sweep${params}`);
}

export async function getStrategyTemplates() {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}templates`);
}

// Zerar Testes (hard): apaga estratégias, automações, ordens e monitores de TESTE.
export async function cleanTestEnvironment() {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}clean-tests`, {});
}

// replace=true: substitui uma estratégia de mesmo nome já existente (limpa a antiga +
// componentes antes de recriar). Enviado após o usuário confirmar o "Substituir".
export async function applyStrategyTemplate(templateId, symbol, config, environment, replace = false) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}templates/apply`, { templateId, symbol, config, environment, replace });
}

// Estratégia paramétrica (símbolo-agnóstica): 1 strategy AI Full -> N ativos base
// sob um quote fixo. bases: ['BTC','ETH',...]; quote: 'USDT' (preferredQuote do usuário).
export async function applyParametricTemplate(templateId, bases, quote, config, environment, replace = false) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}templates/apply`, { templateId, targetMode: 'list', bases, quote, config, environment, replace });
}

// Estratégia automática (Fase 3): o scanner escolhe os ativos; sem lista fixa.
export async function applyAutoTemplate(templateId, quote, config, environment, replace = false) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}templates/apply`, { templateId, targetMode: 'auto', quote, config, environment, replace });
}

// Provisiona um produtor de AI_SIGNAL (automação AI_ANALYSIS agendada) para um símbolo —
// usado quando um short por IA aparece "sem cobertura". Custa tokens de IA por ciclo.
export async function provisionAiSignal(symbol, environment) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}provision-ai`, { symbol, environment });
}

// Provisiona vários símbolos de uma vez (bloco "Provisionar IA" do banner de pendências
// operacionais). EM PARALELO: cada chamada mexe em linhas próprias (userId+type+symbol), sem
// recurso compartilhado entre símbolos diferentes — nada a serializar. Custa tokens × N.
export async function provisionAllAi(symbols) {
    const results = await Promise.allSettled(symbols.map(({ symbol, environment }) => provisionAiSignal(symbol, environment)));
    let created = 0, alreadyCovered = 0;
    const errors = [];
    results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
            if (r.value && r.value.alreadyCovered) alreadyCovered++; else created++;
        } else {
            const message = r.reason?.response?.data?.error || r.reason?.message || 'erro';
            errors.push({ symbol: symbols[i].symbol, message });
        }
    });
    return { created, alreadyCovered, errors };
}

export async function getStrategiesUsage() {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}usage`);
}

export async function exportStrategy(id, format = 'json', orderLimit = 50) {
    const params = new URLSearchParams({ format, orderLimit });
    return axios.get(`${STRATEGIES_URL}${id}/export?${params}`, { responseType: 'blob' });
}

export async function exportStrategySystem(id) {
    return axios.get(`${STRATEGIES_URL}${id}/export/system`, { responseType: 'blob' });
}

export async function importStrategyPreview(data) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}import/preview`, { data });
}

export async function importStrategy(data, resolutions) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}import`, { data, resolutions });
}

export async function evaluateStrategy(id, providerId, opts = {}) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/evaluate`, { providerId, ...opts });
}

// Revisão de POSTURA DE RISCO da conta (read-only). providerId opcional (default = fallback chain).
export async function reviewAccountRisk(opts = {}) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}account-risk-review`, opts);
}

export async function applyEvaluation(id, suggestions, mode = 'direct') {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/evaluate/apply`, { suggestions, mode });
}

// Camada 1 da validação de proposta (Consistente/Inconsistente): roda a bateria de cenários na
// estratégia original vs uma cópia com as sugestões aplicadas (in-process, read-only).
export async function validateEvaluation(id, suggestions) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/evaluate/validate`, { suggestions });
}

// Camada 2 (Aprovada/Reprovada): dispara o backtest A/B em background (retorna pollKey).
export async function scoreEvaluation(id, suggestions, days) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/evaluate/score`, { suggestions, days });
}
// Lê o resultado do backtest A/B (poll): { state: running|done|error|none, result?, error? }.
export async function getEvaluationScore(id) {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}${id}/evaluate/score`);
}

// Propostas da reavaliação semanal por IA (persistidas pelo job). status: pending|applied|dismissed.
export async function getEvaluations(status, market) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (market) params.set('market', market);
    const q = params.toString();
    return await appRequest.doGetRequest(`${STRATEGIES_URL}evaluations${q ? `?${q}` : ''}`);
}

export async function setEvaluationStatus(id, status) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}evaluations/${id}/status`, { status });
}

// Aplica o DIFF DE CONFIG (knobs do template, ex.: stopLossPct) de uma proposta pendente — passa
// pelo mesmo mecanismo do Reatualizar (sim-gate + snapshot). Ver proposal.configDiff (P6.3).
export async function applyEvaluationConfig(id) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}evaluations/${id}/apply-config`, {});
}

// "Avaliar agora" — roda a reavaliação por IA de todas as estratégias ativas sob demanda.
export async function runEvaluationsNow() {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}evaluations/run`, {});
}

export async function dismissAllEvaluations(market) {
    const q = market ? `?market=${encodeURIComponent(market)}` : '';
    return await appRequest.doPostRequest(`${STRATEGIES_URL}evaluations/dismiss-all${q}`, {});
}

export async function getSnapshots(id) {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}${id}/snapshots`);
}

export async function saveSnapshot(id, label) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/snapshots`, { label });
}

export async function restoreSnapshot(id, snapshotId) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/snapshots/${snapshotId}/restore`, {});
}

export async function deleteSnapshot(id, snapshotId) {
    return await appRequest.doDeleteRequest(`${STRATEGIES_URL}${id}/snapshots/${snapshotId}`);
}

// "Reatualizar" (rebase): propaga o template+config atual pra estratégia viva sem perder estado/IDs.
// preview devolve o diff (o que muda em cada automação) SEM aplicar; apply aplica in-place.
export async function reapplyStrategyPreview(id, config) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/reapply?preview=true`, { config });
}

export async function reapplyStrategyApply(id, config) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}${id}/reapply`, { config });
}

// Camada MEME — UX de risco (P5 item 3): aceite explícito do aviso + orçamento em USDT (min 10,
// max 10000). O servidor carimba a data do aceite — nunca envie/considere um acceptedAt do cliente.
export async function acceptMemeLayer(budgetUsdt) {
    return await appRequest.doPostRequest(`${STRATEGIES_URL}meme-layer/accept`, { budgetUsdt });
}

// Estado vivo da camada MEME (orçamento, aceite, disjuntor por camada) — pro card do guia.
export async function getMemeLayerStatus() {
    return await appRequest.doGetRequest(`${STRATEGIES_URL}meme-layer/status`);
}
